import { motion } from "framer-motion";
import { ArrowRight, BarChart3, Brain, Cloud, Layers, MessageCircle, Sparkles, Target } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { ParticleWave } from "./ParticleWave";

const PILLARS = [
  { id: "visao", icon: Target, title: "Visão Estratégica", desc: "Alinhamos tecnologia à estratégia do seu negócio." },
  { id: "erps", icon: Layers, title: "ERPs & Sistemas", desc: "Especialistas nos principais ERPs do mercado: SAP, Oracle, Totvs, Logix e RM." },
  { id: "ia", icon: Brain, title: "IA Aplicada", desc: "Automação inteligente e IA generativa para processos e decisões." },
  { id: "cloud", icon: Cloud, title: "Cloud & Integração", desc: "Arquiteturas modernas, escaláveis e seguras na nuvem." },
  { id: "resultados", icon: BarChart3, title: "Resultados Reais", desc: "Mais eficiência, menos custos, mais controle e crescimento sustentável." },
];

const LOGO_URL =
  "/ponzena-logo.png";

export default function Hero() {
  const c = useContent("hero") || {};
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const openWhats = () => {
    window.open(
      "https://wa.me/5511980331231?text=Ol%C3%A1%20David%2C%20quero%20conversar%20sobre%20consultoria%20em%20TI.",
      "_blank"
    );
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center pt-36 pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Circuit SVG background */}
      <svg
        className="absolute inset-0 w-full h-full opacity-40 circuit-svg"
        viewBox="0 0 1200 800"
        preserveAspectRatio="none"
        fill="none"
      >
        <defs>
          <linearGradient id="grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#2979FF" stopOpacity="0.3" />
          </linearGradient>
        </defs>
        <path
          d="M0,200 L200,200 L220,180 L400,180 L420,200 L600,200"
          stroke="url(#grad)"
          strokeWidth="1.5"
        />
        <path
          d="M1200,150 L1000,150 L980,170 L800,170 L780,150 L650,150"
          stroke="url(#grad)"
          strokeWidth="1.5"
        />
        <path
          d="M0,500 L150,500 L170,520 L350,520 L370,540 L500,540 L520,520 L700,520"
          stroke="url(#grad)"
          strokeWidth="1.2"
        />
        <path
          d="M1200,650 L1050,650 L1030,670 L850,670 L830,650 L700,650"
          stroke="url(#grad)"
          strokeWidth="1.2"
        />
        <circle cx="200" cy="200" r="3" fill="#00E5FF" />
        <circle cx="600" cy="200" r="3" fill="#00E5FF" />
        <circle cx="650" cy="150" r="3" fill="#00E5FF" />
        <circle cx="500" cy="540" r="3" fill="#00E5FF" />
        <circle cx="700" cy="650" r="3" fill="#00E5FF" />
      </svg>

      {/* Big P background echo */}
      <div className="absolute right-[-8%] top-[10%] hidden md:block opacity-[0.06] pointer-events-none">
        <div className="font-display text-[560px] leading-none text-cyan-400 select-none">P</div>
      </div>

      <div className="relative w-full">
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center w-full">
        <div className="lg:col-span-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 px-4 py-2 border border-cyan-400/30 rounded-full bg-cyan-400/5 mb-8"
            data-testid="hero-badge"
          >
            <Sparkles size={14} className="text-cyan-400" />
            <span className="label-eyebrow">{c.badge || "Transformação Digital · ERPs · IA · Processos · Cloud"}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] font-black leading-[0.95] tracking-tight text-white"
            data-testid="hero-title"
          >
            {c.title_line_1 || "Modernize o"}<br />
            <span className="gradient-text">{c.title_line_2 || "backoffice."}</span><br />
            {c.title_line_3 || "Liberte o time."}<br />
            <span className="text-cyan-400 glow-text">{c.title_line_4 || "Escale com IA."}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-8 text-lg md:text-xl text-[#B8C0D0] max-w-2xl leading-relaxed"
            data-testid="hero-subtitle"
          >
            {c.subtitle || "Consultoria Sênior em ERPs, BackOffice e Automação de Processos. Ajudamos empresas a modernizar sistemas, eliminar gargalos e tomar decisões baseado em dados com eficiência e segurança."}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button
              onClick={() => scrollTo("contato")}
              className="btn-primary"
              data-testid="hero-cta-primary"
            >
              {c.cta_primary || "Agendar Diagnóstico"} <ArrowRight size={18} />
            </button>
            <button
              onClick={openWhats}
              className="btn-secondary"
              data-testid="hero-cta-whatsapp"
            >
              <MessageCircle size={18} /> {c.cta_secondary || "Falar com o Consultor"}
            </button>
          </motion.div>

        </div>

        {/* Right visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="lg:col-span-4 hidden lg:flex justify-center"
        >
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-cyan-400/20 blur-3xl scale-150" />
            <div className="relative rounded-2xl border border-cyan-400/25 bg-[#030B1E]/80 backdrop-blur-md p-10 shadow-[0_0_80px_rgba(0,229,255,0.25)] float-y">
              <img
                src="/ponzena-emblem.png"
                alt="Ponzena - Transformação Digital para Empresas"
                className="w-64 h-auto object-contain mx-auto"
                data-testid="hero-logo"
              />
              <div className="mt-6 pt-6 border-t border-cyan-400/15 text-center">
                <p className="font-mono-tech text-[10px] text-cyan-400 tracking-[0.32em] uppercase">
                  // desde 2026
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Pillars strip */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.55 }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 mt-16 w-full"
        data-testid="hero-pillars"
      >
        <div className="rounded-2xl border border-cyan-400/15 bg-[#050F26]/90 backdrop-blur-md grid sm:grid-cols-2 lg:grid-cols-5 divide-y sm:divide-y lg:divide-y-0 lg:divide-x divide-cyan-400/10">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              className="flex items-start gap-4 p-6 group transition-colors hover:bg-cyan-400/[0.04]"
              data-testid={`hero-pillar-${p.id}`}
            >
              <p.icon
                size={30}
                strokeWidth={1.6}
                className="text-cyan-400 shrink-0 mt-0.5 transition-transform group-hover:scale-110 group-hover:drop-shadow-[0_0_10px_rgba(0,229,255,0.6)]"
              />
              <div>
                <p className="text-white font-semibold text-sm md:text-[15px]">{p.title}</p>
                <p className="text-[#8892B0] text-xs md:text-[13px] leading-relaxed mt-1.5">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
      </div>

      {/* Particle wave "bits" — bottom of hero */}
      <ParticleWave height={300} />
    </section>
  );
}
